/**
 * OwnerSettings Component
 * 오너 설정 UI
 */
import React from 'react';
import DisplayBoardCard from './DisplayBoardCard';  // ⭐ TV 전광판 관리 카드
import StudyReportCard from './StudyReportCard';  // ⭐ 순공시간 데일리 리포트 설정
import BellCard from './BellCard';  // ⭐ 교시 종소리 설정
import SubscriptionPlanCard from './SubscriptionPlanCard';  // ⭐ 구독 플랜·베이직 신청
import {
  CogIcon,
  BuildingOfficeIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useOwnerSettings } from '../use-cases/useOwnerSettings';
import { PasswordChangeModal } from '../../auth/components';

const OwnerSettings: React.FC = () => {
  const {
    orgInfo,
    loading,
    copied,
    orgName,
    setOrgName,
    orgDescription,
    setOrgDescription,
    showPasswordChange,
    handleCopyOrgId,
    handleSaveSettings,
    openPasswordChangeModal,
    closePasswordChangeModal,
    handlePasswordChangeSuccess,
    planInfo,
    planLoading,
  } = useOwnerSettings();

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <CogIcon className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">로딩 중...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <CogIcon className="h-8 w-8 mr-3 text-primary-600" />
            시스템 설정
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            독서실 운영에 필요한 기본 설정을 관리하세요.
          </p>
        </div>

        {/* ⭐ 2026-10-27 구독 플랜 — 무료 → 베이직 신청(약관·요금 동의). 기능 제한 없음 */}
        <SubscriptionPlanCard />

        {/* ⭐ 2026-06-21 TV 전광판 (디스플레이 토큰 + TV 공지) */}
        <DisplayBoardCard />

        {/* ⭐ 2026-06-23 순공시간 데일리 리포트 설정 */}
        <StudyReportCard />

        {/* ⭐ 2026-06-30 교시 종소리 설정 */}
        <BellCard />

        {/* Organization ID 정보 */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <InformationCircleIcon className="h-5 w-5 mr-2 text-blue-500" />
              시스템 정보
            </h2>
          </div>

          {/* Organization ID */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-900 mb-1">
                  Organization ID
                </h3>
                {orgInfo?.id ? (
                  <>
                    <p className="text-2xl font-mono font-bold text-blue-700 break-all">
                      {orgInfo.id}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      시스템에서 사용하는 고유 식별자입니다. API 연동 시 필요합니다.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-lg text-red-600 font-semibold">
                      Organization ID가 없습니다
                    </p>
                    <p className="text-xs text-red-500 mt-1">
                      관리자에게 문의하세요. (디버깅: orgInfo = {JSON.stringify(orgInfo)})
                    </p>
                  </>
                )}
              </div>
              {orgInfo?.id && (
                <button
                  onClick={handleCopyOrgId}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ml-4"
                >
                  {copied ? (
                    <>
                      <CheckIcon className="h-4 w-4 mr-2" />
                      복사됨
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                      복사
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* 구독 플랜 정보 */}
          <div className={`${planInfo?.plan === '프리미엄' ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200' : 'bg-green-50 border-green-200'} border rounded-lg p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900 mb-1">
                  구독 플랜
                </h3>
                {planLoading ? (
                  <p className="text-sm text-gray-500">플랜 정보 로딩 중...</p>
                ) : planInfo ? (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <p className={`text-2xl font-bold ${planInfo.plan === '프리미엄' ? 'text-purple-700' : 'text-green-700'}`}>
                        {planInfo.plan}
                      </p>
                      {planInfo.plan === '프리미엄' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          ⭐ PREMIUM
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {planInfo.plan === '프리미엄'
                        ? '모든 기능을 무제한으로 사용할 수 있습니다'
                        : '독서실 운영에 필요한 핵심 기능을 사용할 수 있습니다'}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {planInfo.available_features.slice(0, 5).map((feature: string) => (
                        <span
                          key={feature}
                          className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                            planInfo.plan === '프리미엄'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          ✓ {feature}
                        </span>
                      ))}
                      {planInfo.available_features.length > 5 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                          +{planInfo.available_features.length - 5}개
                        </span>
                      )}
                    </div>
                    {planInfo.plan === '스탠다드' && planInfo.restricted_features.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-green-200">
                        <p className="text-xs text-gray-500 mb-1">
                          프리미엄으로 업그레이드하면 {planInfo.restricted_features.length}개 기능을 추가로 사용할 수 있습니다
                        </p>
                        <button
                          className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                          onClick={() => {
                            // TODO: 업그레이드 모달 또는 페이지로 이동
                            alert('플랜 업그레이드를 원하시면 관리자에게 문의해 주세요.\n\n이메일: admin@newattend.com');
                          }}
                        >
                          프리미엄 플랜 알아보기 →
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">플랜 정보를 불러올 수 없습니다</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 기본 설정 */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2 text-gray-500" />
              기본 정보
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="org-name"
                className="block text-sm font-medium text-gray-700"
              >
                독서실 이름
              </label>
              <input
                type="text"
                id="org-name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="독서실 이름을 입력하세요"
              />
            </div>

            <div>
              <label
                htmlFor="org-description"
                className="block text-sm font-medium text-gray-700"
              >
                설명
              </label>
              <textarea
                id="org-description"
                rows={3}
                value={orgDescription}
                onChange={(e) => setOrgDescription(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="독서실에 대한 간단한 설명을 입력하세요"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveSettings}
              className="inline-flex items-center px-4 py-2 bg-primary-600 border border-transparent shadow-sm text-sm font-medium rounded-md text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              저장
            </button>
          </div>
        </div>

        {/* 보안 설정 */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <LockClosedIcon className="h-5 w-5 mr-2 text-gray-500" />
              보안 설정
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">비밀번호 변경</h3>
              <p className="text-sm text-gray-500 mb-3">
                보안을 위해 정기적으로 비밀번호를 변경하는 것을 권장합니다.
              </p>
              <button
                onClick={openPasswordChangeModal}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <LockClosedIcon className="h-4 w-4 mr-2" />
                비밀번호 변경
              </button>
            </div>
          </div>
        </div>

        {/* 시스템 상태 */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">시스템 상태</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckIcon className="h-5 w-5 text-green-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">백엔드 서버</h3>
                  <p className="text-sm text-green-600">정상 작동</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckIcon className="h-5 w-5 text-green-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">데이터베이스</h3>
                  <p className="text-sm text-green-600">연결됨</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckIcon className="h-5 w-5 text-green-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">실시간 연결</h3>
                  <p className="text-sm text-green-600">활성화</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 저장 버튼 */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveSettings}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            설정 저장
          </button>
        </div>
      </div>

      {/* 비밀번호 변경 모달 */}
      <PasswordChangeModal
        isOpen={showPasswordChange}
        onSuccess={handlePasswordChangeSuccess}
        onClose={closePasswordChangeModal}
      />
    </div>
  );
};

export default OwnerSettings;
